import { useState, useEffect, useCallback } from "react";

/**
 * Simple hook for scroll-based dark/light mode transitions
 * Primarily used for homepage hero section transition
 *
 * @param {Object} options - Configuration options
 * @param {number} options.scrollThreshold - Fraction of viewport height to trigger transition (default: 0.7)
 * @param {boolean} options.enableScrollDetection - Whether to enable scroll detection (default: true)
 * @param {string} options.defaultMode - Default mode ('dark' or 'light', default: 'dark')
 * @returns {Object} - { isDarkMode, shouldBlur }
 */
const useBackgroundDetection = (options = {}) => {
  const {
    scrollThreshold = 0.7,
    enableScrollDetection = true,
    defaultMode = "dark",
  } = options;

  const [isDarkMode, setIsDarkMode] = useState(defaultMode === "dark");
  const [shouldBlur, setShouldBlur] = useState(false);

  const handleScroll = useCallback(() => {
    if (!enableScrollDetection) return;

    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    const transitionPoint = viewportHeight * scrollThreshold;

    // Dark mode at top, light mode after scrolling past threshold
    setIsDarkMode(scrollY < transitionPoint);

    // Add blur effect when scrolled a bit
    setShouldBlur(scrollY > 50);
  }, [enableScrollDetection, scrollThreshold]);

  useEffect(() => {
    if (!enableScrollDetection) {
      // If scroll detection is disabled, just use default
      setIsDarkMode(defaultMode === "dark");
      setShouldBlur(false);
      return;
    }

    // Initial check
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll, enableScrollDetection, defaultMode]);

  return {
    isDarkMode,
    shouldBlur,
  };
};

export default useBackgroundDetection;
